import React, { Dispatch, SetStateAction, useState } from "react"
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import { CREATE_TASK_ENDPOINT, SERVER_URL, TASK_GIS_ENDPOINT } from "@pma/utils/api-routes"
import axios from "axios"
import { DictType } from "@pma/types"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { useMutation } from "@tanstack/react-query"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"
import { cn } from "@pma/utils/helpers"

interface Props {
  projectId?: string
  task?: DictType
  gisData?: DictType
  closeGisPopUp?: Dispatch<SetStateAction<any>>
  group_id?: number | null | undefined
}
const AddSubTaskInput = ({ projectId, task, gisData, closeGisPopUp, group_id = null }: Props) => {
  const [title, setTitle] = useState("")

  const navigate = useNavigate()
  const location = useLocation()

  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const { translate } = useTranslator()

  const paramProjectId = useParams()?.projectId

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const currentTime = Date.now()

  const midnightTime = new Date().setHours(23, 59, 59, 999);

  const isSubItem = task && task?.id

  const addTask = async (payload: DictType) => {
    if (group_id) {
      payload["tasks_group_id"] = group_id > 0 ? group_id : 0
    }
    const parentData = isSubItem
      ? {
          parent_tasks_id: task?.id,
        }
      : {}
    const response = await axios.post(
      SERVER_URL + CREATE_TASK_ENDPOINT,
      {
        ...payload,
        ...parentData,
        start_at: currentTime,
        finish_to: midnightTime,
        project_id: projectId || paramProjectId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  }

  const linkTaskToGisPoint = async (payload: DictType) => {
    const response = await axios.post(
      SERVER_URL + TASK_GIS_ENDPOINT,
      {
        task_id: payload?.id,
        data: gisData,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  }

  const addTaskMutation = useMutation({
    mutationKey: ["add-task"],
    mutationFn: addTask,
    onSuccess: (data) => {
      if (gisData) {
        return linkTaskToGisPointMutation.mutate(data)
      } else {
        // Revalidate tasks when tasks are updated
        toast({
          title: translate("Success"),
          description: translate("Task added successfully"),
          variant: "success",
        })

        newParams.set("revalidate", "tasks")
        queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
        navigate(`?${newParams.toString()}`)
        setTitle("")
      }
    },

    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error adding task"),
        variant: "error",
      })
    },
  })

  const linkTaskToGisPointMutation = useMutation({
    mutationKey: ["add-task-gis-link"],
    mutationFn: linkTaskToGisPoint,
    onSuccess: (data) => {
      // Revalidate tasks
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      setTitle("")
      closeGisPopUp && closeGisPopUp(null)

      toast({
        title: translate("Success"),
        description: translate("Task added successfully"),
        variant: "success",
      })
    },

    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error adding task"),
        variant: "error",
      })
    },
  })
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        addTaskMutation.mutate({ title })
      }}
      className={cn("flex items-center gap-2", gisData && "my-4 w-full gap-1")}
    >
      {addTaskMutation.isPending && (
        <div
          className={cn(
            addTaskMutation.isPending &&
              "absolute h-9 animate-shimmer rounded-md bg-gradient-to-r from-black/10 to-slate-500/10 delay-100 duration-150 sm:w-[250px] md:w-[350px] lg:w-[600px]",
          )}
        />
      )}
      <Input
        placeholder={translate(isSubItem ? "SubItem" : "New task")}
        className='h-9 sm:w-[250px] md:w-[350px] lg:w-[600px]'
        value={title}
        onChange={(e) => setTitle(e?.target?.value)}
      />
      <Button
        size={"sm"}
        loading={addTaskMutation.isPending}
        disabled={addTaskMutation.isPending || !title}
        type='submit'
      >
        {translate(isSubItem ? "Add SubItem" : "Add Task")}
      </Button>
    </form>
  )
}

export default AddSubTaskInput
